// Marquoir : fiche produit (P1). Aperçu en direct coté, deux initiales, finition et longueur, ajout au panier.
// Amélioration de sections/main-product.liquid : sans ce module, la fiche reste utilisable (les pastilles sont de vrais
// radios envoyés en propriétés de commande, la liste des modèles choisit la variante, le formulaire part tel quel).
// Dessin 2D : mq/chiffre-svg (même géométrie que la 3D). Rendu 3D à la demande : mq/chiffre-3d, API v1 ou v2
// (les méthodes v2 sont détectées avant usage). Mouvement réduit : aucune animation et pas de 3D.
// Événements émis sur document : mq:lettres { i1, i2 }, mq:finition { finition }, mq:panier-maj { item_count },
// mq:panier-ajout { item, key, items, sections, remplacement }.
// Ajout : le tiroir s'ouvre sur la réponse de /cart/add.js, qui porte déjà son rendu (paramètre sections) : aucune
// requête en série. /cart.js n'est lu que s'il n'y a pas de tiroir dans la page (compteur de l'en-tête).
// « Modifier » (tiroir, page panier) ouvre la fiche avec &ligne=<clé> : le bouton devient « Mettre à jour le panier »
// et l'envoi remplace cette ligne (ajout du nouveau modèle, puis retrait de l'ancien) au lieu d'en ajouter une.
// Initiales par défaut A et L (comme l'accueil) : l'aperçu n'est jamais vide. Deux cases « Première / Seconde
// initiale » et une seule grille visible ; une lettre touchée dans la première passe d'elle-même à la seconde.
// Galerie en tête (classe Galerie) : piste aimantée, compteur, vignettes et flèches ; l'aperçu y est la 2e diapositive,
// la 3D ne se prépare que lorsqu'elle est à l'écran.

const POSE = 'cubic-bezier(.2, 0, 0, 1)';
const DUREE_PASTILLE = 120;
const DUREE_POSE = 300;
const DUREE_TRACE = 240;
const GLISSEMENT_PX = 8;
const ATTENTE_ANNONCE = 450;
const DUREE_AJOUTE = 2000;
const CAPITALE_PX = 160; // capitale nominale de chiffre-svg.js : mêmes proportions que le dessin complet
const GABARIT = 'N'; // lettre de référence d'un emplacement vide (contour pointillé)
const LETTRE = /^[A-Z]$/;
const HAUTEUR_VIGNETTE = 88; // 72 px + marges : la vignette se retire avant de recouvrir la suite
const DEFAUT = { 1: 'A', 2: 'L' }; // initiales par défaut, comme l'accueil
// Place des cotes que dessine le moteur 3D autour des lettres (px CSS) : à gauche, filet à 24 px et valeur « [xx] mm »
// de 13 px finissant 10 px avant lui (≈ 89 px) ; en bas, filet à 24 px et valeur 22 px plus bas (≈ 50 px).
const COTES_3D_GAUCHE = 92;
const COTES_3D_BAS = 52;
const CARRE_3D_LARGE = 600; // au-delà, la place des cotes est rendue à droite aussi : l'objet reste centré comme le dessin

const requeteReduit = window.matchMedia('(prefers-reduced-motion: reduce)');
const requeteFin = window.matchMedia('(pointer: fine)');
const requeteEtroit = window.matchMedia('(max-width: 989px)');
const mouvementPermis = () => !requeteReduit.matches;
const racineBoutique = () => (window.Shopify && window.Shopify.routes && window.Shopify.routes.root) || '/';
const urlGlyphes = () => (window.Marquoir && window.Marquoir.glyphesUrl) || '';

// ---------- Outils ----------
function nombre(v) {
  return String(Math.round(v * 10) / 10);
}

function normaliserLettre(valeur) {
  const l = String(valeur ?? '').trim().toUpperCase();
  return LETTRE.test(l) ? l : '';
}

// Finition reconnue aux mots de la valeur, dans les cinq langues de la boutique (en, fr, de, es, it) :
// « doree », « Dorée », « gold-tone », « Or », « oro », « goud »… → 'doree' ;
// « argentee », « silver », « Silber », « plateado », « zilver »… → 'argentee'
function normaliserFinition(valeur) {
  const mots = String(valeur ?? '')
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '')
    .toLowerCase()
    .split(/[^a-z]+/)
    .filter(Boolean);
  if (!mots.length) return '';
  if (mots.some((m) => m.startsWith('arg') || m.startsWith('plat') || /silver|silber|zilver/.test(m))) return 'argentee';
  if (mots.some((m) => m.startsWith('dor') || m.includes('gold') || m === 'or' || m === 'oro' || m === 'goud')) return 'doree';
  return '';
}

function webgl2Disponible() {
  try {
    const toile = document.createElement('canvas');
    const gl = toile.getContext('webgl2');
    if (!gl) return false;
    const perte = gl.getExtension('WEBGL_lose_context');
    if (perte) perte.loseContext();
    return true;
  } catch (erreur) {
    return false;
  }
}

// Module de dessin et glyphes, chargés une fois pour toute la page
let promesseDessin = null;
function chargerDessin() {
  if (!promesseDessin) {
    promesseDessin = import('mq/chiffre-svg')
      .then(async (mod) => ({ mod, glyphes: await mod.chargerGlyphes(urlGlyphes()) }))
      .catch((erreur) => {
        promesseDessin = null;
        throw erreur;
      });
  }
  return promesseDessin;
}

function versElement(balise) {
  const gabarit = document.createElement('template');
  gabarit.innerHTML = balise;
  return gabarit.content.firstElementChild;
}

// Mêmes attributs que rendreChiffreSVG : trait encre 1,5 px (dessin) ou aplat cerné d'encre 1 px (rendu)
function attributsLettre(mode, finition) {
  if (mode === 'rendu') {
    const fond = finition === 'argentee' ? 'var(--argent-apercu,#c4c6c8)' : 'var(--or-apercu,#c9a86a)';
    return `stroke-width="1" stroke-linejoin="round" vector-effect="non-scaling-stroke" style="fill:${fond};stroke:var(--encre,#1a1f2b)"`;
  }
  return 'fill="none" stroke-width="1.5" stroke-linejoin="round" vector-effect="non-scaling-stroke" style="stroke:var(--encre,#1a1f2b)"';
}

function cheminLettre(mod, glyphe, dx, dy, tx, ty) {
  let d = '';
  for (const contour of mod.lireContours(glyphe.contours)) {
    for (const [t, ...v] of contour) {
      if (t === 'Z') {
        d += 'Z';
        continue;
      }
      const points = [];
      for (let j = 0; j < v.length; j += 2) points.push(`${tx(v[j] + dx)} ${ty(v[j + 1] + dy)}`);
      d += t + points.join(' ');
    }
  }
  return d;
}

/**
 * Planche incomplète : lettres choisies dessinées, lettres manquantes en emplacement pointillé (gabarit « N »),
 * à la place exacte où elles se poseront (disposer de chiffre-svg.js). viewBox = boîte des deux lettres.
 */
function planchePartielle(mod, glyphes, i1, i2, { mode, finition }) {
  const { lettres, boite } = mod.disposer(glyphes, i1 || GABARIT, i2 || GABARIT);
  const [x0, y0, x1, y1] = boite;
  const k = CAPITALE_PX / glyphes.capHeight;
  const tx = (x) => nombre((x - x0) * k);
  const ty = (y) => nombre((y1 - y) * k);
  const choisies = [i1, i2];
  const suivant = i1 ? 2 : 1; // l'emplacement à remplir ensuite est en graphite, l'autre en filet
  const morceaux = lettres.map((l, n) => {
    if (choisies[n]) {
      return `<path class="mq-chiffre-svg__lettre mq-chiffre-svg__lettre--${n + 1}" d="${cheminLettre(mod, l.glyphe, l.x, l.y || 0, tx, ty)}" ${attributsLettre(mode, finition)}/>`;
    }
    const b = l.glyphe.boite;
    const classe = `mq-apercu__emplacement mq-apercu__emplacement--${n + 1}${n + 1 === suivant ? ' mq-apercu__emplacement--suivant' : ''}`;
    return `<rect class="${classe}" x="${tx(l.x + b[0])}" y="${ty((l.y || 0) + b[3])}" width="${nombre((b[2] - b[0]) * k)}" height="${nombre((b[3] - b[1]) * k)}" vector-effect="non-scaling-stroke"/>`;
  });
  const classe = `mq-chiffre-svg mq-chiffre-svg--${mode === 'rendu' ? 'rendu' : 'dessin'} mq-apercu__svg`;
  return versElement(`<svg class="${classe}" viewBox="0 0 ${nombre((x1 - x0) * k)} ${nombre((y1 - y0) * k)}" focusable="false" aria-hidden="true"><g class="mq-chiffre-svg__lettres">${morceaux.join('')}</g></svg>`);
}

/** Planche complète : rendreChiffreSVG sans ses cotes (celles de la fiche sont en HTML, nettes à toute taille). */
function plancheComplete(mod, glyphes, i1, i2, { mode, finition, titre }) {
  const svg = mod.rendreChiffreSVG(glyphes, i1, i2, { mode, finition, cotes: null, titre });
  svg.classList.add('mq-apercu__svg');
  svg.removeAttribute('width');
  svg.removeAttribute('height');
  return svg;
}

// Une fois dans la page : viewBox recadré sur les lettres, pour que les cotes HTML tombent juste sur leurs bords.
// La bélière dépasse au-dessus du viewBox : renvoie ce dépassement en part de la hauteur des lettres (--fp-dessus),
// que la planche réserve au-dessus du dessin.
function recadrer(svg) {
  const groupe = svg.querySelector('.mq-chiffre-svg__lettres') || svg;
  const beliere = svg.querySelector('.mq-chiffre-svg__beliere');
  try {
    const b = groupe.getBBox();
    if (b.width > 0 && b.height > 0) {
      svg.setAttribute('viewBox', `${nombre(b.x)} ${nombre(b.y)} ${nombre(b.width)} ${nombre(b.height)}`);
      return beliere ? Math.max(0, b.y - beliere.getBBox().y) / b.height : 0;
    }
  } catch (erreur) {
    // SVG pas encore rendu : la marge de chiffre-svg.js reste, sans conséquence
  }
  return 0;
}

function echelleSVG(svg) {
  const vb = svg.viewBox && svg.viewBox.baseVal;
  const largeur = svg.getBoundingClientRect().width;
  return vb && vb.width && largeur ? largeur / vb.width : 1;
}

// Repli si les glyphes ne se chargent pas : les lettres en Newsreader, superposées comme le chiffre
function lettresTexte(i1, i2) {
  const p = document.createElement('p');
  p.className = 'mq-apercu__lettres-texte';
  p.setAttribute('aria-hidden', 'true');
  [i1, i2].forEach((l) => {
    const span = document.createElement('span');
    span.textContent = l || '·';
    p.append(span);
  });
  return p;
}

// ---------- Galerie en tête ----------
// Piste horizontale aimantée (CSS scroll-snap) : la position se lit au défilement (une lecture par image) ; vignettes
// et flèches la font glisser, sans jamais faire défiler la page. Mouvement réduit : saut direct.
class Galerie {
  constructor(racine) {
    this.racine = racine;
    this.piste = racine.querySelector('[data-fp-piste]');
    this.diapos = Array.from(racine.querySelectorAll('[data-fp-diapo]'));
    this.minis = Array.from(racine.querySelectorAll('[data-fp-vers]'));
    this.compteur = racine.querySelector('[data-fp-compteur]');
    this.avant = racine.querySelector('[data-fp-precedente]');
    this.apres = racine.querySelector('[data-fp-suivante]');
    this.index = 0;
    this.image = 0;
    this.nettoyages = [];
    if (!this.piste || this.diapos.length < 2) return;
    const lire = () => {
      this.image = 0;
      const largeur = this.piste.clientWidth || 1;
      this.marquer(Math.round(this.piste.scrollLeft / largeur));
    };
    const surDefilement = () => { if (!this.image) this.image = window.requestAnimationFrame(lire); };
    this.ecouter(this.piste, 'scroll', surDefilement, { passive: true });
    this.minis.forEach((b) => this.ecouter(b, 'click', () => this.allerA(Number(b.dataset.fpVers))));
    this.ecouter(this.avant, 'click', () => this.allerA(this.index - 1));
    this.ecouter(this.apres, 'click', () => this.allerA(this.index + 1));
    this.nettoyages.push(() => { if (this.image) window.cancelAnimationFrame(this.image); });
    this.marquer(0);
  }

  ecouter(cible, type, fn, options) {
    if (!cible) return;
    cible.addEventListener(type, fn, options);
    this.nettoyages.push(() => cible.removeEventListener(type, fn, options));
  }

  indexApercu() {
    return this.diapos.findIndex((d) => d.hasAttribute('data-fp-diapo-apercu'));
  }

  allerA(n, { lisse = true } = {}) {
    if (!this.piste || !this.diapos.length) return;
    const i = Math.max(0, Math.min(this.diapos.length - 1, n));
    const left = i * this.piste.clientWidth;
    const behavior = lisse && mouvementPermis() ? 'smooth' : 'auto';
    if (typeof this.piste.scrollTo === 'function') this.piste.scrollTo({ left, behavior });
    else this.piste.scrollLeft = left;
    this.marquer(i);
  }

  marquer(i) {
    const n = Math.max(0, Math.min(this.diapos.length - 1, i));
    this.index = n;
    if (this.compteur) this.compteur.textContent = String(n + 1).padStart(2, '0');
    this.minis.forEach((b) => {
      if (Number(b.dataset.fpVers) === n) b.setAttribute('aria-current', 'true');
      else b.removeAttribute('aria-current');
    });
    // la vignette active reste visible dans sa colonne (ordinateur), sans faire défiler la page
    const active = this.minis.find((b) => Number(b.dataset.fpVers) === n);
    const colonne = active && active.closest('.fp-media__vignettes');
    if (colonne && colonne.scrollHeight > colonne.clientHeight) {
      const haut = active.offsetTop - colonne.offsetTop;
      if (haut < colonne.scrollTop || haut + active.offsetHeight > colonne.scrollTop + colonne.clientHeight) {
        colonne.scrollTop = haut - colonne.clientHeight / 2 + active.offsetHeight / 2;
      }
    }
    if (this.avant) this.avant.disabled = n === 0;
    if (this.apres) this.apres.disabled = n === this.diapos.length - 1;
  }

  detruire() {
    this.nettoyages.forEach((fn) => fn());
    this.nettoyages = [];
  }
}

// ---------- Fiche ----------
class Fiche {
  constructor(racine) {
    this.racine = racine;
    this.d = racine.dataset;
    this.etat = { i1: '', i2: '', finition: 'doree', mode: 'dessin', traceFait: false };
    this.variante = null;
    this.chiffre3d = null;
    this.chargement3D = null;
    this.premiereImage3D = false;
    this.jetonDessin = 0;
    this.nettoyages = [];
    this.trouver();
    if (this.form) this.init();
  }

  q(selecteur) {
    return this.racine.querySelector(selecteur);
  }

  qa(selecteur) {
    return Array.from(this.racine.querySelectorAll(selecteur));
  }

  ecouter(cible, type, fn, options) {
    if (!cible) return;
    cible.addEventListener(type, fn, options);
    this.nettoyages.push(() => cible.removeEventListener(type, fn, options));
  }

  trouver() {
    this.form = this.q('[data-fp-form]');
    this.radios = { 1: this.qa('[data-fp-lettre="1"]'), 2: this.qa('[data-fp-lettre="2"]') };
    this.groupes = { 1: this.q('[data-fp-groupe="1"]'), 2: this.q('[data-fp-groupe="2"]') };
    this.erreursGroupe = { 1: this.q('[data-fp-erreur-groupe="1"]'), 2: this.q('[data-fp-erreur-groupe="2"]') };
    this.optionsRadios = this.qa('[data-fp-option-radio]');
    this.champVariante = this.q('[data-fp-variante]');
    this.bouton = this.q('[data-fp-ajouter]');
    this.libelle = this.q('[data-fp-libelle]');
    this.erreur = this.q('[data-fp-erreur]');
    this.erreurTexte = this.q('[data-fp-erreur-texte]');
    this.succes = this.q('[data-fp-succes]');
    this.indisponible = this.q('[data-fp-indisponible]');
    this.recap = this.q('[data-fp-recap]');
    this.zoneAnnonce = this.q('[data-fp-annonce]');
    this.boutonInverser = this.q('[data-fp-inverser]');
    this.devant = this.q('[data-fp-devant]');
    this.erreurOrdre = this.q('[data-fp-erreur-ordre]');
    this.composer = this.q('[data-fp-composer]');
    this.zone = this.q('[data-fp-zone]');
    this.planche = this.q('[data-fp-planche]');
    this.dessin = this.q('[data-fp-dessin]');
    this.texteVide = this.q('[data-fp-vide-texte]');
    this.calque3d = this.q('[data-fp-3d]');
    this.modes = this.q('[data-fp-modes]');
    this.boutonsMode = this.qa('[data-fp-mode]');
    this.bouton3d = this.boutonsMode.find((b) => b.dataset.fpMode === '3d') || null;
    this.mention2d = this.q('[data-fp-mention-2d]');
    this.mention3d = this.q('[data-fp-mention-3d]');
    this.statut = this.q('[data-fp-statut]');
    this.vignette = this.q('[data-fp-vignette]');
    this.vignettesDessin = this.qa('[data-fp-vignette-dessin]'); // vignette collée (mobile) et vignette de la galerie
    this.vignetteTexte = this.q('[data-fp-vignette-texte]');
    this.cases = { 1: this.q('[data-fp-case="1"]'), 2: this.q('[data-fp-case="2"]') };
    this.casesLettre = { 1: this.q('[data-fp-case-lettre="1"]'), 2: this.q('[data-fp-case-lettre="2"]') };
    const galerie = this.q('[data-fp-galerie]');
    this.galerie = galerie ? new Galerie(galerie) : null;
  }

  init() {
    this.form.noValidate = true; // validation de la fiche : message rouge-correction, jamais de bouton grisé
    this.libelleInitial = this.libelle ? this.libelle.textContent : '';
    this.variantes = this.lireVariantes();

    // Départ : aucune lettre, sauf radios restaurés par le navigateur ou adresse ?i1=&i2= (accueil, panier)
    const params = new URLSearchParams(window.location.search);
    const urlI1 = normaliserLettre(params.get('i1'));
    const urlI2 = normaliserLettre(params.get('i2'));
    const urlFinition = normaliserFinition(params.get('finition'));
    this.ligneOrigine = this.lireLigneOrigine(params.get('ligne'));
    if (urlI1) this.cocher(1, urlI1);
    if (urlI2) this.cocher(2, urlI2);
    // ni l'adresse ni le navigateur n'en donnent : A et L par défaut, l'aperçu n'est jamais vide
    [1, 2].forEach((n) => { if (!this.lettreCochee(n)) this.cocher(n, DEFAUT[n]); });
    if (urlFinition) {
      const radio = this.optionsRadios.find((r) => r.dataset.fpFinition === urlFinition);
      if (radio) radio.checked = true;
    }
    this.etat.i1 = this.lettreCochee(1);
    this.etat.i2 = this.lettreCochee(2);
    this.majVariante();

    this.ecouter(this.form, 'change', (evt) => this.surChangement(evt));
    this.ecouter(this.form, 'submit', (evt) => this.surEnvoi(evt));
    this.ecouter(this.boutonInverser, 'click', () => this.inverser());
    // une lettre touchée (doigt, souris) dans la première grille passe à la seconde ; au clavier, les flèches
    // parcourent la grille sans la quitter
    this.geste = 'pointeur';
    this.ecouter(this.form, 'pointerdown', () => { this.geste = 'pointeur'; });
    this.ecouter(this.form, 'keydown', () => { this.geste = 'clavier'; });
    [1, 2].forEach((n) => this.ecouter(this.cases[n], 'click', () => this.activerCase(n)));
    this.activerCase(1);
    this.observerApercu();
    this.boutonsMode.forEach((b) => this.ecouter(b, 'click', () => this.choisirMode(b.dataset.fpMode)));

    // 3D : seulement si WebGL 2 et mouvement permis ; le module se prépare dès que le pointeur ou le focus approche
    this.peut3D = this.d.troisD !== 'false' && mouvementPermis() && webgl2Disponible();
    if (this.bouton3d) {
      this.bouton3d.hidden = !this.peut3D;
      const prechauffer = () => { if (this.peut3D) import('mq/chiffre-3d').catch(() => {}); };
      this.ecouter(this.bouton3d, 'pointerenter', prechauffer, { once: true });
      this.ecouter(this.bouton3d, 'focus', prechauffer, { once: true });
    }
    // Bascule Dessin / Rendu / 3D retirée de l'affichage (retour du 15/10/2026 : « garde que la 3D ») : la barre reste
    // masquée ; sans 3D possible (mouvement réduit, WebGL absent), l'aperçu montre le rendu, sans bouton.
    if (this.modes) this.modes.hidden = true;

    this.observerEntete();
    this.initVignette();
    this.ecouter(requeteReduit, 'change', () => this.surMouvement());
    this.ecouter(requeteEtroit, 'change', () => { if (this.mesurerVignette) this.mesurerVignette(); });
    this.ecouter(window, 'pageshow', (evt) => { if (evt.persisted) this.resynchroniser(); });
    if ('ResizeObserver' in window && this.zone) {
      const ro = new ResizeObserver(() => this.cadrer3D());
      ro.observe(this.zone);
      this.nettoyages.push(() => ro.disconnect());
    }

    // finition choisie ailleurs (accueil, panier) : l'aperçu s'ouvre en rendu, pour montrer ce métal
    if (urlFinition && this.optionsRadios.some((r) => r.checked && r.dataset.fpFinition === urlFinition)) {
      this.etat.mode = 'rendu';
      this.boutonsMode.forEach((b) => b.setAttribute('aria-pressed', String(b.dataset.fpMode === 'rendu')));
    }
    // la 3D, que l'on fait tourner à la main, est la vue par défaut dès qu'elle est possible (sinon le dessin)
    if (!this.peut3D && this.etat.mode === 'dessin') this.etat.mode = 'rendu';
    if (this.peut3D) {
      this.etat.mode = '3d';
      this.boutonsMode.forEach((b) => b.setAttribute('aria-pressed', String(b.dataset.fpMode === '3d')));
    }
    this.racine.classList.add('fp--pret');
    this.majModeModification();
    this.majTextes();
    if (urlI1 || urlI2) {
      // venue de l'accueil ou du panier : le chiffre se compose sous les yeux (seconde lettre posée, cotes tracées)
      this.dessiner({ animation: this.etat.i1 && this.etat.i2 ? 'pose' : 'apparition', changee: this.etat.i2 ? 2 : 1 });
      this.annoncer(this.texteAnnonce(), 900);
    } else {
      // A et L par défaut : dessinés sans animation ni annonce (rien n'a encore été choisi)
      this.dessiner();
    }
    this.majAffichage3D();
    if (urlI1 || urlI2 || urlFinition || params.has('ligne')) this.majUrl();
  }

  // ---------- « Modifier » : la ligne du panier que cette fiche remplace ----------
  // La clé vient de l'adresse (&ligne=) ; la quantité, du tiroir rendu dans la page (il porte data-cle et data-quantite).
  // Une clé absente du panier (lien ancien, panier changé ailleurs) ramène à un ajout ordinaire.
  lireLigneOrigine(valeur) {
    const cle = String(valeur || '').trim();
    if (!cle) return null;
    // sans tiroir dans la page (éditeur de thème…), on ne peut pas vérifier : on tente le remplacement, quantité 1
    if (!document.querySelector('mq-zone-panier')) return { cle, quantite: 1 };
    const ligne = Array.from(document.querySelectorAll('mq-zone-panier [data-cle]')).find((li) => li.dataset.cle === cle);
    if (!ligne) return null;
    return { cle, quantite: Math.max(1, parseInt(ligne.dataset.quantite, 10) || 1) };
  }

  majModeModification() {
    this.libelleRepos = this.ligneOrigine ? this.d.libelleModifier || this.libelleInitial : this.libelleInitial;
    if (this.libelle && !(this.bouton && this.bouton.classList.contains('fp-ajouter--ajoute'))) {
      this.libelle.textContent = this.libelleRepos;
    }
  }

  // ---------- Lettres ----------
  lettreCochee(n) {
    const radio = this.radios[n].find((r) => r.checked);
    return radio ? radio.value : '';
  }

  cocher(n, lettre) {
    this.radios[n].forEach((r) => { r.checked = r.value === lettre; });
  }

  surChangement(evt) {
    const cible = evt.target;
    if (!(cible instanceof HTMLInputElement)) return;
    if (cible.dataset.fpLettre) {
      const n = Number(cible.dataset.fpLettre);
      this.effacerErreurGroupe(n);
      this.majLettres({ changee: n });
      if (n === 1 && this.geste !== 'clavier') this.activerCase(2, { focus: true });
      // la galerie montre l'aperçu (glissement de la piste seule, la page ne bouge pas)
      if (this.galerie) {
        const i = this.galerie.indexApercu();
        if (i >= 0 && this.galerie.index !== i) this.galerie.allerA(i, { lisse: this.galerieVisible() });
      }
    } else if (cible.hasAttribute('data-fp-option-radio')) {
      this.majVariante({ parUtilisateur: true });
    }
  }

  majLettres({ changee = 0, forcer = false } = {}) {
    const avant = { i1: this.etat.i1, i2: this.etat.i2 };
    this.etat.i1 = this.lettreCochee(1);
    this.etat.i2 = this.lettreCochee(2);
    if (!forcer && avant.i1 === this.etat.i1 && avant.i2 === this.etat.i2) return;
    const { i1, i2 } = this.etat;
    const complet = Boolean(i1 && i2);
    this.cacherSucces();
    if (this.erreurOrdre) this.erreurOrdre.hidden = true;
    this.dessiner({ animation: complet ? 'pose' : 'apparition', changee });
    this.majTextes();
    this.annoncer(this.texteAnnonce());
    if (this.erreur && !this.erreur.hidden) this.majMessageManque();
    this.majUrl();
    if (complet) {
      if (this.chiffre3d) this.chiffre3d.setLettres(i1, i2, { animer: true });
      document.dispatchEvent(new CustomEvent('mq:lettres', { detail: { i1, i2, source: 'produit' } }));
    }
    if (this.etat.mode === '3d') this.majAffichage3D();
  }

  // ---------- Deux cases, une grille ----------
  activerCase(n, { focus = false } = {}) {
    if (!this.cases[n]) return;
    this.caseActive = n;
    [1, 2].forEach((m) => {
      if (this.cases[m]) this.cases[m].setAttribute('aria-pressed', String(m === n));
      if (this.groupes[m]) this.groupes[m].classList.toggle('fp-abc--active', m === n);
    });
    if (focus) this.cases[n].focus({ preventScroll: true });
  }

  galerieVisible() {
    const piste = this.galerie && this.galerie.piste;
    if (!piste) return false;
    const r = piste.getBoundingClientRect();
    return r.bottom > 0 && r.top < window.innerHeight;
  }

  // La 3D se prépare dès que l'aperçu est à l'écran (1re diapositive de la galerie ; modules préchargés par la section)
  observerApercu() {
    this.apercuVisible = true;
    if (!this.zone || !('IntersectionObserver' in window)) return;
    this.apercuVisible = false;
    const io = new IntersectionObserver((entrees) => {
      entrees.forEach((e) => { this.apercuVisible = e.isIntersecting; });
      if (this.apercuVisible) this.majAffichage3D();
      else if (this.chiffre3d && this.etat.mode === '3d') this.chiffre3d.pause();
    }, { threshold: 0.4 });
    io.observe(this.zone);
    this.nettoyages.push(() => io.disconnect());
  }

  inverser() {
    const { i1, i2 } = this.etat;
    if (!i1 && !i2) {
      if (this.erreurOrdre) this.erreurOrdre.hidden = false;
      this.annoncer(this.d.erreurInverser || '', 0);
      return;
    }
    this.cocher(1, i2);
    this.cocher(2, i1);
    [1, 2].forEach((n) => { if (this.lettreCochee(n)) this.effacerErreurGroupe(n); });
    this.majLettres({ changee: 2 });
  }

  // ---------- Textes ----------
  texteFinition() {
    if (this.etat.finition === 'argentee') return this.d.fArgentee || '';
    return this.d.fDoree || '';
  }

  texteAnnonce() {
    const { i1, i2 } = this.etat;
    if (i1 && i2) {
      return (this.d.annonce || '').replace('__I1__', i1).replace('__I2__', i2).replace('__F__', this.texteFinition());
    }
    if (!i1 && !i2) return this.d.recapVide || '';
    // une seule lettre : phrases traduites (fichiers de langue), « __L__ » remplacé par la lettre
    if (i1) return (this.d.recapPremiere || '').replace('__L__', i1);
    return (this.d.recapSeconde || '').replace('__L__', i2);
  }

  majTextes() {
    const { i1, i2 } = this.etat;
    [1, 2].forEach((n) => { if (this.casesLettre[n]) this.casesLettre[n].textContent = this.etat[`i${n}`] || '·'; });
    const vide = !i1 ? this.d.videPremiere : (!i2 ? this.d.videSeconde : '');
    if (this.texteVide && vide) this.texteVide.textContent = vide;
    const recap = this.texteAnnonce();
    if (this.recap) this.recap.textContent = recap;
    if (this.vignetteTexte) this.vignetteTexte.textContent = recap;
    if (this.devant) this.devant.textContent = i1 && i2 ? (this.d.devant || '').replace('__L__', i2) : '';
  }

  annoncer(texte, attente = ATTENTE_ANNONCE) {
    window.clearTimeout(this.minuteurAnnonce);
    this.minuteurAnnonce = window.setTimeout(() => {
      if (!this.zoneAnnonce) return;
      this.zoneAnnonce.textContent = '';
      window.requestAnimationFrame(() => { this.zoneAnnonce.textContent = texte; });
    }, attente);
  }

  // ---------- Dessin 2D ----------
  modeDessin() {
    return this.etat.mode === 'dessin' ? 'dessin' : 'rendu';
  }

  async dessiner({ animation = null, changee = 0, remplissage = false } = {}) {
    if (!this.dessin || !this.planche) return;
    const jeton = ++this.jetonDessin;
    let outils = null;
    try {
      outils = await chargerDessin();
    } catch (erreur) {
      outils = null;
    }
    if (this.detruit || jeton !== this.jetonDessin) return; // un geste plus récent a déjà redessiné

    const { i1, i2 } = this.etat;
    const complet = Boolean(i1 && i2);
    const mode = this.modeDessin();
    const finition = this.etat.finition;
    let svg = null;
    if (outils) {
      try {
        svg = complet
          ? plancheComplete(outils.mod, outils.glyphes, i1, i2, { mode, finition, titre: this.texteAnnonce() })
          : planchePartielle(outils.mod, outils.glyphes, i1, i2, { mode, finition });
      } catch (erreur) {
        svg = null;
      }
    }
    if (svg) {
      this.dessin.replaceChildren(svg);
      const dessus = complet ? recadrer(svg) : 0;
      const vb = svg.viewBox && svg.viewBox.baseVal;
      if (vb && vb.width && vb.height) this.planche.style.setProperty('--fp-ratio', (vb.width / vb.height).toFixed(4));
      this.planche.style.setProperty('--fp-dessus', dessus.toFixed(4));
    } else {
      this.dessin.replaceChildren(lettresTexte(i1, i2));
    }
    this.planche.classList.toggle('mq-apercu__planche--complet', complet);
    this.majVignetteDessin(svg);

    if (svg && animation && mouvementPermis()) {
      if (complet) {
        this.animerPose(svg);
        if (!this.etat.traceFait) this.tracerCotes(); // les cotes se tracent une seule fois
      } else if (changee) {
        this.animerApparition(svg, changee);
      }
    }
    if (svg && remplissage && mouvementPermis()) this.animerRemplissage(svg);
    if (complet) this.etat.traceFait = true;
    if (this.etat.mode === '3d') this.cadrer3D();
  }

  // Signature : la seconde lettre glisse de 8 px et se pose sur la première en 300 ms
  animerPose(svg) {
    const l2 = svg.querySelector('.mq-chiffre-svg__lettre--2');
    if (!l2 || typeof l2.animate !== 'function') return;
    const decalage = (GLISSEMENT_PX / echelleSVG(svg)).toFixed(2);
    l2.animate(
      [{ transform: `translateX(${decalage}px)`, opacity: 0.35 }, { transform: 'translateX(0px)', opacity: 1 }],
      { duration: DUREE_POSE, easing: POSE },
    );
  }

  animerApparition(svg, n) {
    const lettre = svg.querySelector(`.mq-chiffre-svg__lettre--${n}`);
    if (lettre && typeof lettre.animate === 'function') {
      lettre.animate([{ opacity: 0 }, { opacity: 1 }], { duration: DUREE_PASTILLE, easing: POSE });
    }
  }

  // Puis les cotes se tracent en 240 ms : verticale, horizontale, et les valeurs apparaissent au bout des filets
  tracerCotes() {
    const traitV = this.planche.querySelector('.mq-apercu__cote--v svg');
    const traitH = this.planche.querySelector('.mq-apercu__cote--h svg');
    const moitie = DUREE_TRACE / 2;
    const reglage = (delai) => ({ duration: moitie, delay: delai, easing: POSE, fill: 'backwards' });
    if (traitV && traitV.animate) traitV.animate([{ clipPath: 'inset(0 0 100% 0)' }, { clipPath: 'inset(0 0 0% 0)' }], reglage(DUREE_POSE));
    if (traitH && traitH.animate) traitH.animate([{ clipPath: 'inset(0 100% 0 0)' }, { clipPath: 'inset(0 0% 0 0)' }], reglage(DUREE_POSE + moitie));
    this.planche.querySelectorAll('.mq-cote__valeur').forEach((valeur) => {
      if (valeur.animate) {
        valeur.animate([{ opacity: 0 }, { opacity: 1 }], { duration: DUREE_PASTILLE, delay: DUREE_POSE + DUREE_TRACE, easing: POSE, fill: 'backwards' });
      }
    });
  }

  // Du dessin au rendu : l'aplat de métal apparaît dans le trait
  animerRemplissage(svg) {
    svg.querySelectorAll('.mq-chiffre-svg__lettre').forEach((lettre) => {
      if (lettre.animate) lettre.animate([{ fillOpacity: 0 }, { fillOpacity: 1 }], { duration: DUREE_TRACE, easing: POSE });
    });
  }

  majVignetteDessin(svg) {
    if (!this.vignettesDessin.length) return;
    if (!svg) {
      this.vignettesDessin.forEach((v) => v.replaceChildren());
      return;
    }
    const copie = svg.cloneNode(true);
    // la vignette n'a pas de cotes : son viewBox reprend tout le dessin, bélière comprise, pour tenir dans le carré
    try {
      const b = svg.getBBox();
      if (b.width > 0 && b.height > 0) copie.setAttribute('viewBox', `${nombre(b.x)} ${nombre(b.y)} ${nombre(b.width)} ${nombre(b.height)}`);
    } catch (erreur) {
      // SVG pas encore rendu : le viewBox de la planche reste
    }
    copie.removeAttribute('role');
    copie.removeAttribute('aria-label');
    copie.setAttribute('aria-hidden', 'true');
    const titre = copie.querySelector('title');
    if (titre) titre.remove();
    this.vignettesDessin.forEach((v, k) => v.replaceChildren(k ? copie.cloneNode(true) : copie));
  }

  // ---------- Bascule Dessin / Rendu / 3D ----------
  choisirMode(mode, { remplissage = false } = {}) {
    if (!['dessin', 'rendu', '3d'].includes(mode)) return;
    if (mode === '3d' && !this.peut3D) return;
    const ancien = this.etat.mode;
    if (mode === ancien) return;
    this.etat.mode = mode;
    this.boutonsMode.forEach((b) => b.setAttribute('aria-pressed', String(b.dataset.fpMode === mode)));
    if (mode === '3d') {
      this.dessiner({ remplissage: ancien === 'dessin' });
      this.majAffichage3D();
    } else {
      this.masquer3D();
      this.dessiner({ remplissage: remplissage || (ancien === 'dessin' && mode === 'rendu') });
    }
  }

  statutTexte(texte) {
    if (this.statut) this.statut.textContent = texte || '';
  }

  montrer3D(visible) {
    if (this.zone) this.zone.classList.toggle('mq-apercu--3d', visible);
    if (this.mention2d) this.mention2d.hidden = visible;
    if (this.mention3d) this.mention3d.hidden = !visible;
  }

  masquer3D() {
    this.montrer3D(false);
    this.statutTexte('');
    if (this.chiffre3d) this.chiffre3d.pause();
  }

  majAffichage3D() {
    if (this.etat.mode !== '3d') return;
    if (!this.apercuVisible) return; // préparée quand l'aperçu arrive à l'écran (observerApercu)
    if (!(this.etat.i1 && this.etat.i2)) {
      // tant qu'une initiale manque, la planche (emplacements pointillés) reste au premier plan
      this.montrer3D(false);
      return;
    }
    if (this.chiffre3d) {
      this.chiffre3d.reprendre();
      this.cadrer3D();
      this.montrer3D(this.premiereImage3D);
      return;
    }
    this.lancer3D();
  }

  lancer3D() {
    if (this.chargement3D) return this.chargement3D;
    this.statutTexte(this.d.statutTroisD);
    this.chargement3D = (async () => {
      try {
        const { creerChiffre3D } = await import('mq/chiffre-3d');
        if (this.detruit) return;
        // la première image peut arriver avant la fin de creerChiffre3D : on la retient (même schéma que l'ouverture)
        this.premiereImage3D = false;
        const surPremiereImage = () => {
          this.premiereImage3D = true;
          this.basculer3D();
        };
        this.calque3d.addEventListener('mq:chiffre-premiere-image', surPremiereImage, { once: true });
        const objet = await creerChiffre3D(this.calque3d, {
          glyphesUrl: urlGlyphes(),
          i1: this.etat.i1,
          i2: this.etat.i2,
          finition: this.etat.finition,
          interactif: requeteFin.matches,
          // mesures pas encore relevées (data-sans-cotes) : aucune cote, jamais « [xx] mm »
          cotes: 'sansCotes' in this.d ? null : { hauteur: this.d.hauteur, largeur: this.d.largeur },
          plein: false,
          chaine: true,
          progression: 0,
          qualite: 'auto',
          astuce: this.d.astuceRotation || '',
        });
        if (this.detruit) {
          objet.detruire();
          return;
        }
        this.chiffre3d = objet;
        // lettres ou finition changées pendant le chargement
        objet.setLettres(this.etat.i1, this.etat.i2, { animer: false });
        objet.setFinition(this.etat.finition);
        if (typeof objet.setProgression === 'function') objet.setProgression(0, { immediat: true });
        // l'aperçu, c'est « deux initiales et leurs cotes en mm » : la 3D les garde, entièrement tracées (M18)
        if (!('sansCotes' in this.d)) {
          if (typeof objet.afficherCotes === 'function') objet.afficherCotes(true);
          if (typeof objet.tracerCotes === 'function') objet.tracerCotes(1);
        }
        this.cadrer3D();
        if (this.etat.mode !== '3d') objet.pause();
        this.basculer3D();
      } catch (erreur) {
        this.echec3D();
      } finally {
        this.chargement3D = null;
      }
    })();
    return this.chargement3D;
  }

  basculer3D() {
    if (!this.chiffre3d || !this.premiereImage3D) return;
    if (this.etat.mode !== '3d') return;
    this.statutTexte('');
    this.montrer3D(Boolean(this.etat.i1 && this.etat.i2));
  }

  echec3D() {
    if (this.chiffre3d) {
      try { this.chiffre3d.detruire(); } catch (erreur) { /* déjà libéré */ }
    }
    this.chiffre3d = null;
    this.peut3D = false;
    if (this.calque3d) this.calque3d.replaceChildren();
    const avaitFocus = this.bouton3d && document.activeElement === this.bouton3d;
    if (this.etat.mode === '3d') this.choisirMode('rendu');
    if (this.bouton3d) this.bouton3d.hidden = true;
    if (avaitFocus) {
      const rendu = this.boutonsMode.find((b) => b.dataset.fpMode === 'rendu');
      if (rendu) rendu.focus();
    }
    this.statutTexte(this.d.erreurTroisD);
  }

  // Cadre de repos de l'objet (API v2, px CSS dans le calque), M18 : le carré sous la bande des boutons
  // Dessin / Rendu / 3D, moins la place des cotes (à gauche et en bas) et la marge intérieure. Le moteur y centre
  // pendentif et bélière ; ses cotes tombent dans la place réservée, jamais hors du carré. La chaîne sort par le haut.
  cadrer3D() {
    if (!this.chiffre3d || typeof this.chiffre3d.setCadre !== 'function' || !this.calque3d) return;
    const z = this.calque3d.getBoundingClientRect();
    if (!z.width || !z.height) return;
    const style = window.getComputedStyle(this.zone || this.calque3d);
    const pad = parseFloat(style.getPropertyValue('--pad')) || 16;
    let haut = parseFloat(style.getPropertyValue('--haut')) || 60;
    const bande = this.modes && !this.modes.hidden ? this.modes.getBoundingClientRect() : null;
    if (bande && bande.height) haut = Math.max(haut, bande.bottom - z.top + 8);
    const sansCotes = 'sansCotes' in this.d; // sans cotes : pas de place réservée, l'objet est centré
    const gauche = sansCotes ? pad : Math.max(pad, COTES_3D_GAUCHE);
    const droite = sansCotes || z.width >= CARRE_3D_LARGE ? gauche : pad;
    const w = z.width - gauche - droite;
    const h = z.height - haut - (sansCotes ? pad : Math.max(pad, COTES_3D_BAS));
    if (w <= 0 || h <= 0) return;
    this.chiffre3d.setCadre({ x: Math.round(gauche), y: Math.round(haut), w: Math.round(w), h: Math.round(h) });
  }

  surMouvement() {
    if (!mouvementPermis()) {
      this.peut3D = false;
      if (this.etat.mode === '3d') this.choisirMode('rendu');
      if (this.chiffre3d) {
        this.chiffre3d.detruire();
        this.chiffre3d = null;
      }
      if (this.calque3d) this.calque3d.replaceChildren();
    } else {
      this.peut3D = this.d.troisD !== 'false' && webgl2Disponible();
    }
    if (this.bouton3d) this.bouton3d.hidden = !this.peut3D;
  }

  // ---------- Finition, longueur, variante ----------
  lireVariantes() {
    const champ = this.champVariante;
    if (!champ) return [];
    if (champ.tagName !== 'SELECT') {
      return [{ id: champ.value, options: [], prix: champ.dataset.prix || '', disponible: champ.dataset.disponible !== 'false' }];
    }
    return Array.from(champ.options).map((o) => ({
      id: o.value,
      options: (o.dataset.options || '').split('|'),
      prix: o.dataset.prix || '',
      disponible: o.dataset.disponible !== 'false',
    }));
  }

  choixOptions() {
    const choix = [];
    this.optionsRadios.forEach((r) => {
      if (r.checked) choix[Number(r.dataset.index) - 1] = r.value;
    });
    return choix;
  }

  majVariante({ parUtilisateur = false } = {}) {
    let v = null;
    if (this.optionsRadios.length) {
      const choix = this.choixOptions();
      v = this.variantes.find((x) => x.options.length === choix.length && x.options.every((o, i) => o === choix[i])) || null;
    } else {
      v = this.variantes[0] || null;
    }
    this.variante = v;
    if (v && this.champVariante) this.champVariante.value = v.id;
    if (v && v.prix) this.qa('[data-fp-prix]').forEach((el) => { el.textContent = v.prix; });
    if (this.indisponible) this.indisponible.hidden = Boolean(v && v.disponible);

    const radioFinition = this.optionsRadios.find((r) => r.checked && r.dataset.fpFinition);
    const ancienne = this.etat.finition;
    this.etat.finition = radioFinition ? radioFinition.dataset.fpFinition : 'doree';
    if (!parUtilisateur) return;

    this.cacherSucces();
    // un message « modèle indisponible » ne survit pas au changement de modèle
    if (this.erreur && !this.erreur.hidden) this.majMessageManque();
    this.majUrl();
    if (this.etat.finition !== ancienne) {
      // le métal choisi se voit tout de suite : le dessin au trait passe en rendu
      if (this.etat.mode === 'dessin') this.choisirMode('rendu', { remplissage: true });
      else this.dessiner();
      if (this.chiffre3d) this.chiffre3d.setFinition(this.etat.finition);
      this.majTextes();
      this.annoncer(this.texteAnnonce());
      document.dispatchEvent(new CustomEvent('mq:finition', { detail: { finition: this.etat.finition, source: 'produit' } }));
    }
  }

  majUrl() {
    if (!window.history || typeof window.history.replaceState !== 'function') return;
    try {
      const url = new URL(window.location.href);
      const { i1, i2 } = this.etat;
      if (i1) url.searchParams.set('i1', i1);
      else url.searchParams.delete('i1');
      if (i2) url.searchParams.set('i2', i2);
      else url.searchParams.delete('i2');
      url.searchParams.delete('finition');
      if (this.variante && this.optionsRadios.length) url.searchParams.set('variant', this.variante.id);
      if (this.ligneOrigine) url.searchParams.set('ligne', this.ligneOrigine.cle);
      else url.searchParams.delete('ligne');
      window.history.replaceState(window.history.state, '', url.toString());
    } catch (erreur) {
      // adresse inchangée
    }
  }

  // ---------- Erreurs : un message dit ce qui manque, le focus va au groupe ----------
  majMessageManque() {
    const manque = [1, 2].filter((n) => !this.etat[`i${n}`]);
    if (!manque.length) {
      this.effacerErreur();
      return;
    }
    let texte = this.d.erreurDeux;
    if (manque.length === 1) texte = manque[0] === 1 ? this.d.erreurPremiere : this.d.erreurSeconde;
    this.montrerErreur(texte);
  }

  montrerErreur(texte) {
    if (!this.erreur || !this.erreurTexte) return;
    this.erreurTexte.textContent = texte || '';
    this.erreur.hidden = false;
    if (this.bouton) this.bouton.setAttribute('aria-describedby', this.erreur.id);
  }

  effacerErreur() {
    if (this.erreur) this.erreur.hidden = true;
    if (this.bouton) this.bouton.removeAttribute('aria-describedby');
  }

  effacerErreurGroupe(n) {
    const groupe = this.groupes[n];
    if (!groupe) return;
    groupe.classList.remove('fp-abc--erreur');
    if (this.cases[n]) this.cases[n].classList.remove('fp-case--erreur');
    if (this.erreursGroupe[n]) this.erreursGroupe[n].hidden = true;
    this.radios[n].forEach((r) => {
      r.removeAttribute('aria-invalid');
      r.removeAttribute('aria-describedby');
    });
    const reste = [1, 2].some((m) => this.groupes[m] && this.groupes[m].classList.contains('fp-abc--erreur'));
    if (!reste && this.zone) this.zone.classList.remove('mq-apercu--erreur');
  }

  signalerManque(manque) {
    this.activerCase(manque[0]);
    manque.forEach((n) => { if (this.cases[n]) this.cases[n].classList.add('fp-case--erreur'); });
    manque.forEach((n) => {
      const groupe = this.groupes[n];
      const message = this.erreursGroupe[n];
      if (groupe) groupe.classList.add('fp-abc--erreur');
      if (message) message.hidden = false;
      this.radios[n].forEach((r) => {
        r.setAttribute('aria-invalid', 'true');
        if (message) r.setAttribute('aria-describedby', message.id);
      });
    });
    this.majMessageManque();
    if (this.zone) this.zone.classList.add('mq-apercu--erreur');
    const premier = manque[0];
    const cible = this.radios[premier][0];
    if (cible) this.amenerA(this.groupes[premier], cible);
  }

  amenerA(bloc, cible) {
    const r = bloc.getBoundingClientRect();
    // même retrait que le navigateur : scroll-padding de la page (en-tête + 8 px) plus scroll-margin du bloc
    const retrait = parseFloat(getComputedStyle(document.documentElement).scrollPaddingTop) || 0;
    const haut = retrait + (parseFloat(getComputedStyle(bloc).scrollMarginTop) || 0);
    const visible = r.top >= haut - 1 && r.bottom <= window.innerHeight;
    if (!visible) {
      const lisse = mouvementPermis();
      const lenis = window.Marquoir && window.Marquoir.motion && window.Marquoir.motion.lenis;
      if (lenis && typeof lenis.scrollTo === 'function') lenis.scrollTo(bloc, { offset: -haut, immediate: !lisse });
      else bloc.scrollIntoView({ block: 'start', behavior: lisse ? 'smooth' : 'auto' });
    }
    cible.focus({ preventScroll: true });
  }

  // ---------- Ajout au panier ----------
  surEnvoi(evt) {
    evt.preventDefault();
    if (this.envoiEnCours) return;
    const manque = [1, 2].filter((n) => !this.etat[`i${n}`]);
    if (manque.length) {
      this.signalerManque(manque);
      return;
    }
    if (!this.variante || !this.variante.disponible) {
      this.montrerErreur(this.d.indisponible);
      if (this.indisponible) this.indisponible.hidden = true; // un seul message à la fois
      return;
    }
    this.effacerErreur();
    this.ajouter();
  }

  // POST au panier (Ajax API) : JSON en retour, message du serveur en cas de refus
  async envoyerPanier(url, { formulaire = null, json = null } = {}) {
    const entetes = { Accept: 'application/json', 'X-Requested-With': 'XMLHttpRequest' };
    if (json) entetes['Content-Type'] = 'application/json';
    const reponse = await fetch(url, {
      method: 'POST',
      credentials: 'same-origin',
      headers: entetes,
      body: json ? JSON.stringify(json) : formulaire,
    });
    const donnees = await reponse.json().catch(() => ({}));
    if (!reponse.ok) throw new Error(donnees.description || donnees.message || '');
    return donnees;
  }

  async ajouter() {
    this.envoiEnCours = true;
    if (this.bouton) this.bouton.setAttribute('aria-busy', 'true');
    this.cacherSucces();
    const racine = racineBoutique();
    const remplacer = this.ligneOrigine;
    try {
      const quantite = remplacer ? remplacer.quantite : 1;
      const corps = new FormData();
      corps.append('id', this.variante.id);
      corps.append('quantity', String(quantite));
      // noms des propriétés de commande : ceux des pastilles, traduits par la fiche (« Initial 1 », « Initiale 1 »…)
      corps.append(this.radios[1][0].name, this.etat.i1);
      corps.append(this.radios[2][0].name, this.etat.i2);
      // le tiroir reçoit son rendu dans la réponse même (sections) : il s'ouvre sans autre requête.
      // En remplacement, c'est la réponse du retrait de l'ancienne ligne qui porte le rendu final.
      const zones = Array.from(document.querySelectorAll('mq-zone-panier[data-section]')).map((z) => z.dataset.section).filter(Boolean);
      const sections = [...new Set(zones)];
      if (sections.length && !remplacer) corps.append('sections', sections.join(','));
      const item = await this.envoyerPanier(`${racine}cart/add.js`, { formulaire: corps });
      const detail = { item, key: item.key, items: [item], source: 'produit' };
      if (item.sections && typeof item.sections === 'object') detail.sections = item.sections;

      if (remplacer) {
        // même modèle et mêmes lettres : l'ajout s'est fondu dans la ligne d'origine, qui revient à sa quantité ;
        // sinon l'ancienne ligne est retirée. L'ajout passe d'abord : un échec ne fait jamais perdre la ligne.
        const meme = item.key === remplacer.cle;
        const total = Number(item.quantity) || 2 * quantite;
        try {
          const corpsRetrait = { id: remplacer.cle, quantity: meme ? Math.max(1, total - quantite) : 0 };
          if (sections.length) corpsRetrait.sections = sections;
          const panier = await this.envoyerPanier(`${racine}cart/change.js`, { json: corpsRetrait });
          if (panier.sections && typeof panier.sections === 'object') detail.sections = panier.sections;
        } catch (erreur) {
          // ligne d'origine déjà partie : l'ajout seul est juste, le tiroir relira le panier
          delete detail.sections;
        }
        detail.remplacement = true;
        this.ligneOrigine = { cle: item.key, quantite: meme ? remplacer.quantite : quantite };
      }

      if (document.querySelector('mq-tiroir-panier')) {
        // le tiroir applique le rendu reçu, met lui-même à jour le compteur de l'en-tête et s'ouvre
        document.dispatchEvent(new CustomEvent('mq:panier-ajout', { detail }));
      } else {
        // sans tiroir : le compteur de l'en-tête vient de /cart.js
        let compte = null;
        try {
          const panier = await fetch(`${racine}cart.js`, { credentials: 'same-origin', headers: { Accept: 'application/json' } }).then((r) => r.json());
          compte = typeof panier.item_count === 'number' ? panier.item_count : null;
        } catch (erreur) {
          compte = null;
        }
        document.dispatchEvent(new CustomEvent('mq:panier-maj', { detail: compte === null ? { source: 'produit' } : { item_count: compte, source: 'produit' } }));
        document.dispatchEvent(new CustomEvent('mq:panier-ajout', { detail }));
      }
      this.confirmerAjout(Boolean(remplacer));
      if (remplacer) this.majUrl();
    } catch (erreur) {
      const texte = erreur && erreur.message ? erreur.message : this.d.erreurAjout;
      this.montrerErreur(texte || this.d.erreurAjout);
      this.annoncer(texte || this.d.erreurAjout, 0);
    } finally {
      this.envoiEnCours = false;
      if (this.bouton) this.bouton.removeAttribute('aria-busy');
    }
  }

  confirmerAjout(remplacement = false) {
    const repos = this.libelleRepos || this.libelleInitial;
    if (this.libelle) this.libelle.textContent = (remplacement ? this.d.misAJour : this.d.ajoute) || repos;
    if (this.bouton) this.bouton.classList.add('fp-ajouter--ajoute');
    // Le tiroir montre et annonce l'ajout : le message sous le bouton n'apparaît que sans tiroir. Il ne pousse donc
    // plus la promesse ni les accordéons pendant que le tiroir s'ouvre (aucun décalage de mise en page).
    if (!document.querySelector('mq-tiroir-panier')) {
      if (this.succes) this.succes.hidden = false;
      this.annoncer((remplacement ? this.d.misAJourAnnonce : this.d.ajouteAnnonce) || '', 0);
    }
    window.clearTimeout(this.minuteurAjoute);
    this.minuteurAjoute = window.setTimeout(() => {
      if (this.libelle) this.libelle.textContent = this.libelleRepos || this.libelleInitial;
      if (this.bouton) this.bouton.classList.remove('fp-ajouter--ajoute');
    }, DUREE_AJOUTE);
  }

  cacherSucces() {
    if (this.succes) this.succes.hidden = true;
  }

  // ---------- En-tête et vignette ----------
  // L'en-tête (header.js) se cache en descendant : la colonne collée et la vignette suivent sa hauteur visible
  observerEntete() {
    const entete = document.querySelector('mq-entete');
    if (!entete) {
      this.racine.style.setProperty('--fp-decalage', '0px');
      return;
    }
    const maj = () => {
      const cache = entete.hasAttribute('data-cache');
      const hauteur = Math.round(entete.getBoundingClientRect().height) || 64;
      this.racine.style.setProperty('--fp-decalage', cache ? '0px' : `${hauteur}px`);
    };
    maj();
    const observateur = new MutationObserver(maj);
    observateur.observe(entete, { attributes: true, attributeFilter: ['data-cache'] });
    this.nettoyages.push(() => observateur.disconnect());
  }

  // Vignette de 96 px : bande collée en tête des abécédaires. « Collée » (le haut du bloc est passé sous le bord
  // de l'écran) : elle suit l'en-tête quand il revient ; près de la fin du bloc, elle repart avec lui.
  // Mesure au défilement (une lecture par image, écrans étroits seulement) : un observateur d'intersection
  // manquerait les sauts (ancre, focus, défilement programmé).
  initVignette() {
    if (!this.vignette || !this.composer) return;
    let attente = 0;
    const mesurer = () => {
      attente = 0;
      if (!requeteEtroit.matches) {
        this.hautPasse = false;
        this.basPasse = false;
      } else {
        const r = this.composer.getBoundingClientRect();
        this.hautPasse = r.top < 0;
        this.basPasse = r.bottom < 64 + HAUTEUR_VIGNETTE;
      }
      this.majVignette();
    };
    const demander = () => { if (!attente) attente = window.requestAnimationFrame(mesurer); };
    this.mesurerVignette = demander;
    this.ecouter(window, 'scroll', demander, { passive: true });
    this.ecouter(window, 'resize', demander, { passive: true });
    this.nettoyages.push(() => { if (attente) window.cancelAnimationFrame(attente); });
    mesurer();
  }

  majVignette() {
    if (!this.vignette) return;
    const collee = requeteEtroit.matches && this.hautPasse && !this.basPasse;
    this.vignette.classList.toggle('fp-vignette--collee', Boolean(collee));
  }

  // Retour par l'historique : les radios ont pu être restaurés
  resynchroniser() {
    this.majVariante();
    this.majLettres({ forcer: true });
  }

  detruire() {
    this.detruit = true;
    window.clearTimeout(this.minuteurAnnonce);
    window.clearTimeout(this.minuteurAjoute);
    this.nettoyages.forEach((fn) => fn());
    this.nettoyages = [];
    if (this.galerie) this.galerie.detruire();
    if (this.chiffre3d) {
      try { this.chiffre3d.detruire(); } catch (erreur) { /* déjà libéré */ }
      this.chiffre3d = null;
    }
  }
}

// ---------- Démarrage (et éditeur de thème) ----------
const fiches = new WeakMap();

function demarrer(racine = document) {
  racine.querySelectorAll('[data-fp]').forEach((el) => {
    if (!fiches.has(el)) fiches.set(el, new Fiche(el));
  });
}

function arreter(racine) {
  racine.querySelectorAll('[data-fp]').forEach((el) => {
    const fiche = fiches.get(el);
    if (fiche) fiche.detruire();
    fiches.delete(el);
  });
}

demarrer();
document.addEventListener('shopify:section:load', (evt) => demarrer(evt.target));
document.addEventListener('shopify:section:unload', (evt) => arreter(evt.target));
